import { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_VERSION } from '../appVersion';

export const APP_VERSION_RESUME_KEY = 'app-version-resume-url';

const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${publicUrl}/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { version?: string; buildId?: string };
    return (data.version || data.buildId || '').trim() || null;
  } catch {
    return null;
  }
}

/** Router path (/authentication/verify) → absolute URL path (/Kurumsal/authentication/verify) */
function toAbsoluteAppPath(url: string): string {
  if (!url) return publicUrl || '/';
  if (!publicUrl) return url.startsWith('/') ? url : `/${url}`;
  if (url === publicUrl || url.startsWith(`${publicUrl}/`)) return url;
  if (url.startsWith('/')) return `${publicUrl}${url}`;
  return `${publicUrl}/${url}`;
}

/** Absolute URL path → router path (basename'siz) */
function toRouterPath(url: string): string {
  if (!publicUrl) return url || '/';
  if (url === publicUrl) return '/';
  if (url.startsWith(`${publicUrl}/`)) return url.slice(publicUrl.length) || '/';
  return url;
}

/**
 * /login URL'i cache'ten eski SPA shell döndürebilir.
 * Gerçek index.html dosyasını taze çekip, hedef route'u sessionStorage'da tutar.
 */
async function forceLoadLatest(redirectUrl?: string): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // temizlik başarısız olsa da navigasyona devam
  }

  Object.keys(sessionStorage)
    .filter((key) => key.startsWith('app-version-reloaded:'))
    .forEach((key) => sessionStorage.removeItem(key));

  const resume = toAbsoluteAppPath(
    redirectUrl ||
      `${window.location.pathname}${window.location.search}${window.location.hash}` ||
      '/'
  );

  sessionStorage.setItem(APP_VERSION_RESUME_KEY, resume);
  // Statik dosya olarak index.html → no-cache ile taze shell + yeni main.[hash].js
  window.location.replace(`${publicUrl}/index.html?t=${Date.now()}`);
}

/**
 * Login success sonrası, hedef route'a gitmeden önce çağrılır.
 * /version.json farklıysa güncel index.html ile yeniler; aynıysa false döner.
 */
export async function ensureLatestAppVersion(
  _baselineMainScript?: string | null,
  redirectUrl?: string
): Promise<boolean> {
  if (!APP_VERSION || APP_VERSION === 'local') return false;

  const remoteVersion = await fetchRemoteVersion();
  if (!remoteVersion || remoteVersion === APP_VERSION) return false;

  await forceLoadLatest(redirectUrl);
  return true;
}

/**
 * index.html?t=... ile yenilendikten sonra hedef route'a dön.
 * Router içinde (basename altında) mount edilmeli.
 */
export function AppVersionResume() {
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const resume = sessionStorage.getItem(APP_VERSION_RESUME_KEY);
    if (!resume) return;

    sessionStorage.removeItem(APP_VERSION_RESUME_KEY);
    navigate(toRouterPath(resume), { replace: true });
  }, [navigate]);

  return null;
}
